import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { loginRequestSchema, verifyOtpRequestSchema, type LoginRequest, type VerifyOtpRequest } from "@taras/contracts";
import { apiClient } from "../../lib/api/client.js";

type LoginApiResponse = {
  step?: "otp_required";
  challenge_token?: string;
  utente?: { must_change_password: boolean };
};

const otpFormSchema = verifyOtpRequestSchema.omit({ challenge_token: true });
type OtpForm = z.infer<typeof otpFormSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [challengeToken, setChallengeToken] = useState<string | null>(null);

  const loginForm = useForm<LoginRequest>({
    resolver: zodResolver(loginRequestSchema),
  });

  const otpForm = useForm<OtpForm>({
    resolver: zodResolver(otpFormSchema),
  });

  const loginMutation = useMutation({
    mutationFn: (data: LoginRequest) =>
      apiClient.post<LoginApiResponse>("/api/v1/auth/login", data),
    onSuccess: (data) => {
      if (data.step === "otp_required" && data.challenge_token) {
        setChallengeToken(data.challenge_token);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate(searchParams.get("next") ?? "/");
    },
    onError: () => {
      loginForm.setError("password", { message: "Credenziali non valide" });
    },
  });

  const otpMutation = useMutation({
    mutationFn: (data: OtpForm) =>
      apiClient.post<{ utente: unknown }>("/api/v1/auth/login/verify-otp", {
        challenge_token: challengeToken ?? "",
        code: data.code,
      } satisfies VerifyOtpRequest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate(searchParams.get("next") ?? "/");
    },
    onError: () => {
      otpForm.setError("code", { message: "Codice non valido o scaduto" });
    },
  });

  const card = (
    <div className="w-full max-w-sm bg-white rounded-xl shadow-sm border border-gray-200 p-8">
      <div className="text-center mb-8">
        <img src="/taranto-stemma.png" alt="Stemma Comune di Taranto" className="w-20 h-20 mx-auto mb-4 object-contain" />
        <h1 className="text-2xl font-bold text-gray-900">TARAS</h1>
        <p className="text-sm text-gray-500 mt-1">Comune di Taranto</p>
      </div>
      {challengeToken ? (
        <form onSubmit={otpForm.handleSubmit((data) => otpMutation.mutate(data))} className="space-y-4">
          <p className="text-sm text-gray-600 text-center">
            Abbiamo inviato un codice di verifica alla tua email. Inseriscilo qui sotto.
          </p>
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
              Codice di verifica
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-center tracking-widest text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              {...otpForm.register("code")}
            />
            {otpForm.formState.errors.code && (
              <p className="text-xs text-red-600 mt-1">{otpForm.formState.errors.code.message}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={otpMutation.isPending}
            className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {otpMutation.isPending ? "Verifica in corso..." : "Verifica"}
          </button>
          <button
            type="button"
            onClick={() => { setChallengeToken(null); otpForm.reset(); }}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
          >
            ← Torna al login
          </button>
        </form>
      ) : (
        <form onSubmit={loginForm.handleSubmit((data) => loginMutation.mutate(data))} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              {...loginForm.register("username")}
            />
            {loginForm.formState.errors.username && (
              <p className="text-xs text-red-600 mt-1">{loginForm.formState.errors.username.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              {...loginForm.register("password")}
            />
            {loginForm.formState.errors.password && (
              <p className="text-xs text-red-600 mt-1">{loginForm.formState.errors.password.message}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={loginMutation.isPending}
            className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {loginMutation.isPending ? "Accesso in corso..." : "Accedi"}
          </button>
        </form>
      )}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      {card}
    </div>
  );
}
